import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconCheckCircleFilled, IconFileClock } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import { VerifyNewBankAccountModal } from '../../../modals/addPaymentMethodModal/linkBankAccount/verifyNewBankAccountModal/VerifyNewBankAccountModal';
import StepItem from '../stepItem/StepItem';

const StepAction = ({
    status,
    setModalOpen
}: {
    status: string;
    setModalOpen: (modalOpen: boolean) => void;
}) => {
    if (status === 'COMPLETED') {
        return (
            <div className="px-l max-m:py-s">
                <IconCheckCircleFilled
                    data-testid="completed-action-icon"
                    height="24"
                    width="24"
                    fill="rgba(0, 77, 51, 1)"
                    xlinkTitle="checked"
                />
            </div>
        );
    }

    if (status === 'PENDING') {
        return (
            <Button
                variant="primary"
                disabled
                className="min-w-[124px]"
                data-testid="upload-bank-evidence-button"
            >
                <IconFileClock />
            </Button>
        );
    }

    return (
        <Button
            onClick={() => {
                tracking.ampli.fundingStepCtaClicked({
                    funding_step: 'Upload Bank Evidence'
                });
                setModalOpen(true);
            }}
            variant="primary"
            data-testid="upload-bank-evidence-button"
        >
            <FormattedMessage
                id="_.funding_requirements.upload_bank_evidence_button"
                defaultMessage="Verify"
            />
        </Button>
    );
};

const UploadBankEvidenceCaption = ({ status }: { status: string }) => {
    if (status === 'COMPLETED') {
        return (
            <FormattedMessage
                id="_.funding_requirements.upload_bank_evidence_caption_completed"
                defaultMessage="Your bank information has been reviewed."
            />
        );
    }

    if (status === 'PENDING') {
        return (
            <FormattedMessage
                id="_.funding_requirements.upload_bank_evidence_caption_pending"
                defaultMessage="We’re reviewing your bank information and will let you know if we need anything else."
            />
        );
    }

    return (
        <FormattedMessage
            id="_.funding_requirements.upload_bank_evidence_caption"
            defaultMessage="Connect via Open Banking or upload the last 3 months of bank statements."
        />
    );
};

export const UploadBankEvidenceAction = ({
    status,
    triggerRefetchFundingRequirements
}: {
    status: string;
    triggerRefetchFundingRequirements: () => void;
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleModalClose = () => {
        setIsModalOpen(false);
        triggerRefetchFundingRequirements();
    };

    return (
        <>
            <StepItem
                title={
                    <FormattedMessage
                        id="_.funding_requirements.upload_bank_evidence_title"
                        defaultMessage="Link your bank account or upload statements"
                    />
                }
                caption={<UploadBankEvidenceCaption status={status} />}
                action={
                    <StepAction status={status} setModalOpen={setIsModalOpen} />
                }
            />
            <VerifyNewBankAccountModal
                isModalOpen={isModalOpen}
                onClose={handleModalClose}
                isBankEvidence={true}
            />
        </>
    );
};
